import { PageLoader } from '@/components/PageLoader';
import { Form, Input, InputNumber, Select } from 'antd';
import { useState } from 'react';

import { apiFetch } from '@/api/client';
import { AvailabilityBlockTypeValues, type AvailabilityBlockType } from '@/api/generated/enums';
import {
  availabilityKey,
  itemUnitsKey,
  useAvailabilitySearch,
  useItemVariantAxes,
  useItems,
  useUnitCalendar,
  type AvailabilityBlock,
  type AvailableUnit,
} from '@/api/masters';
import { submitVia, useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { Field } from '@/components/Field';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { DatePicker } from '@/components/DatePicker';
import { enterKeyScope } from '@/components/enterKeyNavigation';
import { FormDrawer } from '@/components/FormDrawer';
import { HeldByLine } from '@/components/HeldBy';
import { IconAction } from '@/components/IconAction';
import { GarmentTimeline } from './units/GarmentTimeline';
import { Card, EmptyState, Notice, PageHeading } from '@/components/PageHeading';
import { StatusBadge, type StatusTone } from '@/components/StatusBadge';
import { WriteAction } from '@/components/WriteAction';
import { absent, formatDate, toIsoDate, toDayjs } from '@core/format/dates';
import { chargeableDays, priceGarment } from '@core/bookings/pricing';
import { humanizeEnum } from '@core/format/text';
import type { Session } from '@core/session/types';

import { HoldDrawer } from './bookings/HoldDrawer';

/**
 * The availability and booking-conflict engine (spec Section 7).
 *
 * <p>
 * The spec calls this the hardest algorithmic component in a rental business, and the screen's job
 * is to make one specific thing impossible to misread: <strong>a garment that is not free is not
 * the same as a garment that does not exist.</strong> So the search returns both, and the taken
 * ones carry what is in the way and the date they come back. A counter clerk asking "have you got a
 * navy 40 for the 12th" needs "no, but it is back on the 15th" far more often than an empty list,
 * and an empty list is what sends them to a competitor.
 * </p>
 *
 * <p>
 * The other thing it has to surface is the turnaround buffer. A three-day rental with a two-day
 * cleaning buffer takes the garment off the rail for four days, and a shop that only sees the
 * rental dates will keep wondering why the calendar disagrees with it. The buffer is stated in the
 * results header before anything is committed, and the block it creates is labelled on the calendar
 * rather than looking like a longer booking.
 * </p>
 */
export function AvailabilityPage({ session }: { session: Session }) {
  const [itemId, setItemId] = useState<number | undefined>();
  const [fromDate, setFromDate] = useState<string | undefined>();
  const [toDate, setToDate] = useState<string | undefined>();
  const [axisValueIds, setAxisValueIds] = useState<number[]>([]);

  const [calendarFor, setCalendarFor] = useState<AvailableUnit | null>(null);
  const [timelineFor, setTimelineFor] = useState<AvailableUnit | null>(null);
  const [blocking, setBlocking] = useState<AvailableUnit | null>(null);
  const [chasing, setChasing] = useState<{ unit: AvailableUnit; hold: AvailabilityBlock } | null>(
    null,
  );

  // Inactive designs included: a retired design still has garments coming back, and a booking
  // extension against one has to be answerable.
  const items = useItems(session.branch?.id, { includeInactive: true, pageSize: 200 });

  const axes = useItemVariantAxes(itemId);

  const search = useAvailabilitySearch(session.branch?.id, {
    itemId,
    fromDate,
    toDate,
    axisValueIds: axisValueIds.length > 0 ? axisValueIds : undefined,
  });

  // The searched span, which is what the rate column prices. Availability is asked about possession
  // and a booking is charged for the event days, so this is indicative rather than a quote — but it
  // is the same card and the same arithmetic the counter will use.
  const days = chargeableDays(fromDate ?? '', toDate ?? '');

  const columns: DataViewColumn<AvailableUnit>[] = [
    {
      title: 'Garment',
      dataIndex: 'serialCode',
      primary: true,
      render: (_, row) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-mono-nums font-semibold text-gray-900">{row.serialCode}</span>
            {row.free ? (
              <StatusBadge tone="positive">Free</StatusBadge>
            ) : tentativeHold(row) ? (
              // Not "Committed": nobody has committed. This is the row a clerk can do something
              // about, and it has to look different from the one they cannot.
              <StatusBadge tone="warning">On hold</StatusBadge>
            ) : (
              <StatusBadge tone="negative">Committed</StatusBadge>
            )}
          </div>
          <span className="text-[12px] text-default">
            {row.variantDisplayName} · {humanizeEnum(row.condition)}
          </span>
        </div>
      ),
    },
    {
      title: 'In the way',
      key: 'blockedBy',
      render: (_, row) =>
        row.free ? (
          <span className="text-[12px] text-default">Nothing</span>
        ) : (
          <div className="flex flex-col gap-1">
            {row.blockedBy.map((block) => (
              <BlockLine key={block.id} block={block} />
            ))}
          </div>
        ),
    },
    {
      title: 'Back on',
      dataIndex: 'nextFreeFrom',
      width: 130,
      render: (_, row) =>
        row.nextFreeFrom === null ? (
          <span className="text-default">{absent}</span>
        ) : (
          // The alternate-date suggestion, and the reason a taken garment is worth listing at all.
          <span className="font-mono-nums">{formatDate(row.nextFreeFrom)}</span>
        ),
    },
    {
      title: 'Rate',
      key: 'rate',
      width: 130,
      align: 'right',
      responsive: ['lg'],
      render: (_, row) => {
        // What this garment costs for the dates that were actually searched, through the same
        // function the counter and the server price with. It used to be the variant's base rate,
        // which had no duration in it and was not what any booking was charged.
        const charge = priceGarment(row.rateCard, days);

        return charge === null ? (
          <span className="text-default">{absent}</span>
        ) : (
          <span className="font-mono-nums">
            {charge.amount.toLocaleString('en-IN')}
            {charge.adjustmentPct !== 0 && (
              <span className="ms-1.5 text-[11px] text-default">
                {charge.adjustmentPct > 0 ? '+' : ''}
                {charge.adjustmentPct}%
              </span>
            )}
          </span>
        );
      },
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      align: 'right',
      fixed: 'right',
      trailing: true,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          {/* Only where there is a lapsing claim to act on. A firm booking is managed from the
              booking itself — cancelling somebody's confirmed wedding order is not a thing to
              offer from a search result. */}
          {tentativeHold(row) && (
            <IconAction
              icon="icon icon-phone-call"
              label={`Chase the hold on ${row.serialCode}`}
              onClick={() => setChasing({ unit: row, hold: tentativeHold(row)! })}
            />
          )}

          <IconAction
            icon="icon icon-calendar-days"
            label={`Calendar for ${row.serialCode}`}
            onClick={() => setCalendarFor(row)}
          />

          {/* The calendar beside it answers what is coming; this answers what happened. §5 names
              this row as one of the three places the timeline is reached from. */}
          <IconAction
            icon="icon icon-history"
            label={`History of ${row.serialCode}`}
            onClick={() => setTimelineFor(row)}
          />

          <WriteAction session={session} required="Availability:Create">
            {(disabled) => (
              <IconAction
                icon="icon icon-lock"
                label={`Take ${row.serialCode} off the rail`}
                disabled={disabled || !row.free}
                onClick={() => setBlocking(row)}
              />
            )}
          </WriteAction>
        </div>
      ),
    },
  ];

  const asked = itemId !== undefined && !!fromDate && !!toDate;
  const result = search.data;

  return (
    <>
      <PageHeading
        title="Availability"
        subtitle="Which garments can take a booking, and when the rest come back."
      />

      <Card flush className="mb-4 p-4 max-sm:p-3">
        {/*
          Design, then From, then To, then the axes — four or five controls a clerk works through
          while a customer waits, which is exactly what `enterKeyNavigation` is for. There is no
          commit button to hand the last one to and none is wanted: the search runs as soon as the
          three required answers are there. An open Select or calendar keeps Enter for its own
          highlighted option, which antd reports through `aria-expanded`.
        */}
        <div {...enterKeyScope} className="flex flex-wrap items-end gap-3 max-sm:gap-2">
          <Field label="Item" className="min-w-64 max-sm:min-w-0 max-sm:flex-[1_0_100%]">
            <Select
              value={itemId ?? null}
              onChange={(value: number | null) => {
                setItemId(value ?? undefined);
                // The axes belong to the design's category, so anything chosen against the previous
                // one means nothing here.
                setAxisValueIds([]);
              }}
              options={(items.data?.items ?? []).map((item) => ({
                label: `${item.name} (${item.code})`,
                value: item.id,
              }))}
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder="Which design?"
              className="w-full"
            />
          </Field>

          <Field label="From" className="max-sm:flex-1">
            <DatePicker
              value={toDayjs(fromDate) ?? null}
              onChange={(value) => setFromDate(toIsoDate(value))}
              className="w-full"
            />
          </Field>

          <Field label="To" className="max-sm:flex-1">
            <DatePicker
              value={toDayjs(toDate) ?? null}
              onChange={(value) => setToDate(toIsoDate(value))}
              className="w-full"
            />
          </Field>

          {(axes.data ?? []).map((axis) => (
            <Field
              key={axis.attributeId}
              label={axis.attributeName}
              className="min-w-40 max-sm:min-w-0 max-sm:flex-1"
            >
              <Select
                mode="multiple"
                value={axis.values
                  .filter((value) => axisValueIds.includes(value.valueId))
                  .map((value) => value.valueId)}
                onChange={(chosen: number[]) => {
                  const others = axisValueIds.filter(
                    (id) => !axis.values.some((value) => value.valueId === id),
                  );

                  setAxisValueIds([...others, ...chosen]);
                }}
                options={axis.values.map((value) => ({
                  label: value.name,
                  value: value.valueId,
                }))}
                placeholder="Any"
                className="w-full"
                maxTagCount="responsive"
              />
            </Field>
          ))}
        </div>
      </Card>

      {search.isError && <ApiErrorAlert error={search.error} />}

      {!asked && (
        <Card>
          <EmptyState
            icon="icon icon-calendar-check"
            title="Pick a design and the dates"
            hint="Availability is asked about one design at a time — a whole shop over a month is a calendar, not a search."
          />
        </Card>
      )}

      {asked && result && (
        <>
          <Notice
            tone={result.freeCount > 0 ? 'success' : 'warning'}
            title={
              result.freeCount > 0
                ? `${result.freeCount} of ${result.units.length} free for these dates`
                : `Nothing free — ${result.units.length} garment(s) all committed`
            }
          >
            {result.turnaroundBufferDays > 1 ? (
              <>
                A booking here also takes {result.turnaroundBufferDays - 1} day
                {result.turnaroundBufferDays - 1 === 1 ? '' : 's'} of cleaning afterwards, so the
                garment is off the rail until{' '}
                {/* Stated before anything is committed, not after the calendar grows a block
                    the shop did not expect. */}
                <strong>{formatDate(addDays(result.toDate, result.turnaroundBufferDays))}</strong>.
              </>
            ) : (
              <>This design needs no cleaning time between rentals.</>
            )}
          </Notice>

          <Card flush className="mt-4 p-4 pb-1 max-sm:p-3">
            <DataView<AvailableUnit>
              columns={columns}
              dataSource={result.units}
              loading={search.isFetching}
              rowKey="unitId"
              empty={
                <EmptyState
                  icon="icon icon-shirt"
                  title="No garments match"
                  hint={
                    axisValueIds.length > 0
                      ? 'Nothing is stocked in that configuration at this branch. Try clearing the size or colour.'
                      : 'This design has no garments at this branch yet. Book stock in from Item units.'
                  }
                />
              }
            />
          </Card>
        </>
      )}

      {calendarFor && (
        <CalendarDrawer unit={calendarFor} session={session} onClose={() => setCalendarFor(null)} />
      )}

      {timelineFor && (
        <GarmentTimeline
          unitId={timelineFor.unitId}
          serialCode={timelineFor.serialCode}
          subtitle={timelineFor.variantDisplayName}
          onClose={() => setTimelineFor(null)}
        />
      )}

      {blocking && fromDate && toDate && (
        <BlockDrawer
          unit={blocking}
          fromDate={fromDate}
          toDate={toDate}
          onClose={() => setBlocking(null)}
        />
      )}

      {chasing?.hold.heldBy && (
        <HoldDrawer
          serialCode={chasing.unit.serialCode}
          variantDisplayName={chasing.unit.variantDisplayName}
          holder={chasing.hold.heldBy}
          fromDate={chasing.hold.fromDate}
          toDate={chasing.hold.toDate}
          session={session}
          onClose={() => setChasing(null)}
        />
      )}
    </>
  );
}

/**
 * The lapsing claim on a garment, when there is exactly one to act on.
 *
 * A garment blocked twice over — held for one customer in March and booked for another in April —
 * is not a case the counter can resolve with one phone call, so the row offers the calendar instead
 * of pretending there is a single party behind it.
 */
function tentativeHold(unit: AvailableUnit): AvailabilityBlock | null {
  const holds = unit.blockedBy.filter(
    (block) => block.isTentative && block.heldBy !== null && !block.isBuffer,
  );

  return holds.length === 1 ? (holds[0] ?? null) : null;
}

/* -------------------------------------------------------------------------- */

/** A labelled control in the filter bar. The template has no form-row markup for a toolbar. */
// See `components/Field.tsx`. This copy was the furthest from the template — a muted 12px label
// against the template's 14px slate — which on the search toolbar read as chrome rather than fields.

/**
 * One entry on a calendar, as a line of prose.
 *
 * The buffer is called out rather than shown as another block, because "out on rent until the 14th"
 * and "being cleaned until the 16th" read as one four-day absence otherwise — and the shop starts
 * believing its own rentals are longer than they are.
 */
function BlockLine({ block }: { block: AvailabilityBlock }) {
  return (
    <span className="flex flex-col gap-0.5">
      <span className="flex flex-wrap items-center gap-1.5 text-[12px]">
        <StatusBadge tone={blockTone(block)}>{blockLabel(block)}</StatusBadge>

        <span className="font-mono-nums text-default">
          {formatDate(block.fromDate)} – {formatDate(block.toDate)}
        </span>

        {block.isReleased && <span className="text-[11px] text-default">lifted</span>}
        {block.isExpired && !block.isReleased && (
          <span className="text-[11px] text-default">expired</span>
        )}
        {block.reason && !block.heldBy && <span className="text-default">· {block.reason}</span>}
      </span>

      {/* The party, when there is one. A booking's own `reason` is "Booking BK-2026-27-0001" —
          which identifies the document and not the person, and says nothing at all for a hold,
          which takes no number. */}
      {block.heldBy && !block.isReleased && (
        <HeldByLine holder={block.heldBy} tentative={block.isTentative} className="ps-0.5" />
      )}
    </span>
  );
}

/**
 * What the row is called.
 *
 * <p>
 * <strong>"On hold" and "Booked" are the same block type</strong> — a tentative booking places a
 * `Booking` row carrying the order's expiry, and confirming it only drops that expiry. Labelling
 * from the type alone is what had a shop reading "Booking" against a claim that was one unreturned
 * phone call from lapsing, and turning away a customer who was ready to pay.
 * </p>
 */
function blockLabel(block: AvailabilityBlock): string {
  if (block.isBuffer) {
    return 'Cleaning buffer';
  }

  if (block.blockType === 'Booking') {
    return block.isTentative ? 'On hold' : 'Booked';
  }

  return humanizeEnum(block.blockType);
}

/**
 * A row a rental order placed and is still relying on.
 *
 * <p>
 * All three types count. A booking's turnaround block carries the same booking's ref, and lifting
 * the cleaning without the booking is how a garment goes back on the rail wet. An `Alteration` block
 * placed by the booking flow is the same shape of mistake one further on: the garment is at the
 * tailor and its line still says so, and freeing the dates from here would offer it to the next
 * customer while it is in a workshop. That one is put back through the garment — mark it ready —
 * exactly as a booking's is lifted through the booking.
 * </p>
 *
 * <p>
 * `refId` is what separates those from the ones a person placed here by hand, which carry none and
 * stay liftable: the shop's own maintenance, transit and hold rows, and an alteration somebody
 * blocked out without a booking behind it.
 * </p>
 */
function ownedByABooking(block: AvailabilityBlock): boolean {
  return (
    block.refId !== null &&
    (block.blockType === 'Booking' ||
      block.blockType === 'Cleaning' ||
      block.blockType === 'Alteration') &&
    !block.isExpired
  );
}

/**
 * A released or expired entry is drawn quietly: it is on the calendar as history, and painting it
 * like a live block makes the rail look fuller than it is.
 */
function blockTone(block: AvailabilityBlock): StatusTone {
  if (block.isReleased || block.isExpired) {
    return 'neutral';
  }

  switch (block.blockType) {
    // A firm booking is the brand tone; a tentative one is a warning, because it is the row a clerk
    // is meant to act on rather than accept.
    case 'Booking':
      return block.isTentative ? 'warning' : 'brand';
    case 'Hold':
      return 'warning';
    case 'Maintenance':
      return 'negative';
    default:
      return 'warning';
  }
}

/* -------------------------------------------------------------------------- */

/** One garment's calendar, and where an entry is lifted early. */
function CalendarDrawer({
  unit,
  session,
  onClose,
}: {
  unit: AvailableUnit;
  session: Session;
  onClose: () => void;
}) {
  const [form] = Form.useForm();

  const today = new Date().toISOString().slice(0, 10);
  const horizon = addDays(today, 180);

  const calendar = useUnitCalendar(unit.unitId, today, horizon);

  const release = useApiMutation<unknown, AvailabilityBlock>({
    mutationFn: (block) =>
      apiFetch(`/api/availability/block/${block.id}/release`, {
        method: 'POST',
        body: { reason: null },
      }),
    success: 'Lifted.',
    invalidates: [availabilityKey, itemUnitsKey],
  });

  const blocks = calendar.data?.blocks ?? [];

  return (
    <FormDrawer
      title={unit.serialCode}
      subtitle={`${calendar.data?.itemName ?? ''} · ${unit.variantDisplayName}`}
      open
      onClose={onClose}
      form={form}
      onSubmit={onClose}
      submitText="Close"
      width={560}
    >
      {calendar.isError && <ApiErrorAlert error={calendar.error} />}

      {calendar.isLoading && <PageLoader compact label="Loading availability" />}

      {!calendar.isLoading && blocks.length === 0 && (
        <EmptyState
          icon="icon icon-calendar-check"
          title="Nothing on the calendar"
          hint="This garment is free for the next six months."
        />
      )}

      <ol className="flex flex-col gap-3">
        {blocks.map((block) => (
          <li
            key={block.id}
            className="flex items-start justify-between gap-3 border-s-2 border-border-color ps-3"
          >
            <BlockLine block={block} />

            {!block.isReleased && (
              <WriteAction session={session} required="Availability:Edit">
                {(disabled) => (
                  <IconAction
                    icon="icon icon-unlock"
                    label={
                      ownedByABooking(block)
                        ? block.blockType === 'Alteration'
                          ? 'At the tailor — mark the garment ready on its booking instead'
                          : 'Held by a booking — cancel or confirm that instead'
                        : `Lift ${humanizeEnum(block.blockType)}`
                    }
                    // A booking's rows are not the calendar's to lift. Freeing the dates here would
                    // leave the booking believing it still holds the garment — its garments stay
                    // Allocated against a block that no longer applies — so the shop would have an
                    // order for stock it has given away. The booking's own stage endpoint releases
                    // the block, its turnaround row and the garments' claim together.
                    disabled={disabled || release.isPending || ownedByABooking(block)}
                    onClick={() => void release.mutateAsync(block).catch(() => undefined)}
                  />
                )}
              </WriteAction>
            )}
          </li>
        ))}
      </ol>

      <Notice tone="info" title="Nothing here is deleted">
        Lifting an entry frees the dates but keeps the row, so &ldquo;why was this garment
        unavailable last Tuesday&rdquo; still has an answer.
      </Notice>
    </FormDrawer>
  );
}

/* -------------------------------------------------------------------------- */

/* -------------------------------------------------------------------------- */

interface BlockValues {
  blockType: AvailabilityBlockType;
  fromDate: unknown;
  toDate: unknown;
  reason?: string | null;
  holdMinutes?: number | null;
}

/**
 * Taking a garment off the rail for a reason that is not a booking.
 *
 * `Booking` is deliberately absent from the list. A booking belongs to the rental order that owns
 * it — one placed here would carry nothing for a cancellation to find, and the garment would sit
 * blocked forever. The server refuses it too; leaving it out of the dropdown is so nobody has to
 * discover that by being told off.
 */
function BlockDrawer({
  unit,
  fromDate,
  toDate,
  onClose,
}: {
  unit: AvailableUnit;
  fromDate: string;
  toDate: string;
  onClose: () => void;
}) {
  const [form] = Form.useForm<BlockValues>();

  const blockType = Form.useWatch('blockType', form) ?? 'Maintenance';

  const save = useApiMutation<unknown, BlockValues>({
    mutationFn: (values) =>
      apiFetch('/api/availability/block', {
        method: 'POST',
        body: {
          unitId: unit.unitId,
          fromDate: toIsoDate(values.fromDate),
          toDate: toIsoDate(values.toDate),
          blockType: values.blockType,
          reason: values.reason || null,
          holdMinutes: values.blockType === 'Hold' ? (values.holdMinutes ?? null) : null,
        },
      }),
    success: `${unit.serialCode} taken off the rail.`,
    invalidates: [availabilityKey, itemUnitsKey],
    onSuccess: onClose,
  });

  return (
    <FormDrawer<BlockValues>
      title={`Hold back ${unit.serialCode}`}
      subtitle={unit.variantDisplayName}
      open
      onClose={onClose}
      form={form}
      initialValues={{
        blockType: 'Maintenance',
        fromDate: toDayjs(fromDate),
        toDate: toDayjs(toDate),
        holdMinutes: 120,
      }}
      onSubmit={(values) => void submitVia(save, values)}
      submitText="Take off the rail"
      submitting={save.isPending}
    >
      <Form.Item name="blockType" label="Reason">
        <Select
          options={AvailabilityBlockTypeValues.filter((value) => value !== 'Booking').map(
            (value) => ({ label: humanizeEnum(value), value }),
          )}
        />
      </Form.Item>

      <Form.Item name="fromDate" label="From" rules={[{ required: true, message: 'Pick a date.' }]}>
        <DatePicker className="w-full" />
      </Form.Item>

      <Form.Item name="toDate" label="To" rules={[{ required: true, message: 'Pick a date.' }]}>
        <DatePicker className="w-full" />
      </Form.Item>

      {blockType === 'Hold' && (
        <Form.Item
          name="holdMinutes"
          label="Expires after"
          extra="A hold that never expires keeps the garment off the rail through a wedding weekend, so this is compulsory."
          rules={[{ required: true, message: 'Say how long the hold lasts.' }]}
        >
          <InputNumber min={1} max={7 * 24 * 60} addonAfter="minutes" className="w-full" />
        </Form.Item>
      )}

      <Form.Item name="reason" label="Note">
        <Input.TextArea rows={2} placeholder="Torn hem, back from the tailor Friday" />
      </Form.Item>

      <Notice tone="info" title="Bookings are made from a rental order">
        Committing a garment to a customer creates the block itself, so cancelling the order can
        release it again. That is why Booking is not in the list above.
      </Notice>
    </FormDrawer>
  );
}

/* -------------------------------------------------------------------------- */

/**
 * Adds days to a `YYYY-MM-DD` string, staying in that form throughout.
 *
 * Deliberately not `new Date(iso)` plus arithmetic: that parses as UTC midnight and formats in the
 * viewer's zone, so a date west of Greenwich renders as the day before. These are calendar days at
 * the shop, and the whole reason the column is a `date` is to keep them out of that trap.
 */
function addDays(iso: string, days: number): string {
  const [year, month, day] = iso.split('-').map(Number);

  const shifted = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, (day ?? 1) + days));

  return shifted.toISOString().slice(0, 10);
}
